import { mutate } from 'swr'
import axios from '@fs/zion-axios'
import type { FamilyMembers, PersonId } from '@fs/zion-tree-types'
import { usePersonPageParams } from '../person-page/personPageRouteUtil'
import type { SwrResponse } from './useSwrRequest'
import { updateRequest, useSwrRequest } from './useSwrRequest'
import { mutateChangeLogSummary } from './useChangeLogSummary'
import { mutateAllChildren } from './useChildren'
import { familyMembersCache, swrKey } from './usePersonFamilyMembers'

export default function useFocusFamilyMembers(): SwrResponse<FamilyMembers> & {
  focusFamilyMembers: FamilyMembers
} {
  const { pid } = usePersonPageParams('full')
  const cache = familyMembersCache
  const response = useSwrRequest<FamilyMembers>(swrKey(pid), cache)

  return {
    ...response,
    focusFamilyMembers: response.data ?? { spouses: [], parents: [] },
  }
}

export async function addCoupleRelationship(
  parent1Id: PersonId,
  parent2Id: PersonId,
  focusPersonId: PersonId
): Promise<void> {
  const url = `/service/tree/tree-data/person/${parent1Id}/relationships/spouses`
  const payload = {
    excludeUnknownChildren: true,
    spouseId: parent2Id,
    spouseInPositionOne: false,
  }

  await updateRequest(swrKey(focusPersonId), undefined, () => axios.post(url, payload))
}

export function mutateFocusFamilyMembers(pid: PersonId): void {
  mutate(swrKey(pid))
  mutateChangeLogSummary(pid)
}

/**
 * Revalidates the lists that render a person's portrait alongside the banner: their spouses and
 * parents, plus the children of every expanded couple, where they appear as a spouse and a child.
 */
export function mutateFamilyMemberPortraits(pid: PersonId): Promise<unknown> {
  return Promise.all([mutate(swrKey(pid)), mutateAllChildren()])
}
